import React from "react";
import {
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { ImpactCell, RiskFactorsCell } from "~/components/Cells";
import { ImpactUpdated } from "~/components/impact/Updated/impact-updated";
import { TitleCell } from "~/pages/inventory/components/Findings/FindingsTable/TitleCell";
import { Clamp } from "../clamp/clamp";

import {
  getHeaders,
  getRowUrl,
} from "~/components/ExceptionFindingsTable/exceptionFindingsTableUtils";
import {
  ExceptionFindingsTableException,
  ExceptionFindingsTableScope,
  ExceptionFindingTableType,
} from "./exceptionFindigsTableTypes";
import { Asset } from "~/pages/inventory/asset/types";
import { AssetAdvisoryException } from "~/pages/findings/Exceptions/types";
import { DataTable } from "~/components/data-table";
import { EXCEPTIONS_SCORE_STATES } from "~/pages/findings/Exceptions/constants";

interface ExceptionTableProps {
  findingType: ExceptionFindingTableType;
  scope: ExceptionFindingsTableScope;
  findings: ExceptionFindingsTableException[];
  contextProps?: {
    spaceId?: string;
    assetId?: string;
  };
}

export const ExceptionFindingsTable: React.FC<ExceptionTableProps> = ({
  findingType,
  scope,
  findings,
  contextProps = {},
}) => {
  const navigate = useNavigate();

  const handleRowClick = (exception: ExceptionFindingsTableException) => {
    const rowUrl = getRowUrl(findingType, scope, exception, contextProps);
    navigate(rowUrl);
  };

  function renderTitleCell(exception: ExceptionFindingsTableException) {
    if (findingType === "asset") {
      return (
        <TableCell>
          <Clamp lines={2}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                lineHeight: "16px",
              }}
            >
              {(exception as Asset).name}
            </Typography>
          </Clamp>
        </TableCell>
      );
    }

    return (
      <TitleCell
        id={(exception as AssetAdvisoryException)?.id}
        title={(exception as AssetAdvisoryException).title}
      />
    );
  }

  const headers = getHeaders(findingType);

  return (
    <DataTable
      id={`${scope}-${findingType}`}
      sx={{
        borderRadius: `4px !important`,
        overflow: "hidden",
        "& tr:last-child td": {
          borderBottom: "none",
        },
      }}
    >
      <TableHead>
        <TableRow>
          {headers.map((header, index) => (
            <TableCell
              key={`${header?.field}-${index}`}
              width={header?.options?.width}
              sx={header?.options?.sx}
            >
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {findings.map((finding: any) => (
          <TableRow
            sx={{
              cursor: "pointer",
            }}
            onClick={() => handleRowClick(finding)}
            key={finding.id || finding.mrn}
          >
            {findingType === "asset" && (
              <TableCell sx={{ width: "100px" }}>
                <ImpactUpdated
                  impact={{
                    value: finding.score.riskValue,
                    rating: finding.score.riskRating,
                  }}
                  isRiskScore
                />
              </TableCell>
            )}
            {findingType !== "asset" && (
              <ImpactCell
                rating={finding.rating}
                impact={finding.riskValue || 0}
                isActive
              />
            )}
            {renderTitleCell(finding)}
            <RiskFactorsCell
              riskFactors={finding.riskFactors}
              isActive={!EXCEPTIONS_SCORE_STATES.includes(finding.state)}
            />
          </TableRow>
        ))}
      </TableBody>
    </DataTable>
  );
};
